import type { FullMetadata } from 'firebase/storage'
import {
  RenderFormat,
  ViewerDocumentKind,
  ViewerDownloadTargetKind,
  ViewerOpenContextKind,
  type ContentViewerSession,
  type FileContentRendererConfig,
  type ViewerOpenContext,
  type ViewerPdfRenderAsset,
  type ViewerMarkdownRenderAsset,
} from '@/content-viewer/store/schemas'
import { createViewerSession } from '@/content-viewer/utils/create-viewer-session'
import { kGcsUserFileUploadsBucketRef, kGcsUserMarkdownFilesBucketRef } from '@/constants/constants-gcs'
import { buildLegacyFileRendererConfig } from './build-legacy-file-renderer-config'
import { normalizeStoragePath } from '@/chat-common/reference-storage/resolve-reference-storage'

type LegacyFileViewerContext = Extract<
  ViewerOpenContext,
  {
    kind:
      | ViewerOpenContextKind.DRIVE_FILE
      | ViewerOpenContextKind.SELECTED_FILE
      | ViewerOpenContextKind.MED_CHRON_REFERENCE
      | ViewerOpenContextKind.MED_CHRON_SOURCE
      | ViewerOpenContextKind.MED_SUMMARY_REFERENCE
      | ViewerOpenContextKind.MED_SUMMARY_SOURCE
  }
>

type BuildLegacyFileViewerSessionArgs = {
  id: string
  title: string
  renderer: FileContentRendererConfig
  context: LegacyFileViewerContext
  downloadable?: boolean
}

function buildLegacyFileRenderAsset(renderer: FileContentRendererConfig): ViewerPdfRenderAsset | ViewerMarkdownRenderAsset {
  const filePath = normalizeStoragePath(renderer.filePath) || renderer.filePath

  if (renderer.format === RenderFormat.PDF) {
    return {
      format: RenderFormat.PDF,
      filePath,
      gcsBucketRef: kGcsUserFileUploadsBucketRef,
      legacyAlwaysCloud: true,
    }
  }

  return {
    format: RenderFormat.MARKDOWN,
    filePath,
    gcsBucketRef: kGcsUserMarkdownFilesBucketRef,
    legacyAlwaysCloud: true,
  }
}

export function buildLegacyFileViewerSession(args: BuildLegacyFileViewerSessionArgs): ContentViewerSession {
  const { id, title, renderer, context, downloadable = true } = args
  const renderAsset = buildLegacyFileRenderAsset(renderer)

  return createViewerSession({
    document: {
      kind: ViewerDocumentKind.LEGACY_FILE,
      id,
      title,
      renderAsset,
      ...(downloadable
        ? {
            downloadTarget: {
              kind: ViewerDownloadTargetKind.LEGACY_OWNER_FILE,
              filePath: renderer.filePath.endsWith('.md') ? renderer.filePath.slice(0, -3) : renderer.filePath,
            },
          }
        : {}),
    },
    context,
    presentation: {
      ...(renderer.format === RenderFormat.PDF
        ? { pdfHighlights: renderer.highlights, startingPage: renderer.startingPage }
        : { markdownHighlights: renderer.highlights ?? undefined }),
    },
  })
}

export function buildLegacyFileViewerSessionFromMetadata(fileMetadata: FullMetadata, context: LegacyFileViewerContext): ContentViewerSession | null {
  const renderer = buildLegacyFileRendererConfig(fileMetadata)
  if (!renderer) return null

  return buildLegacyFileViewerSession({
    id: fileMetadata.fullPath,
    title: fileMetadata.name,
    renderer,
    context,
  })
}
